import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import { parseNumber } from '@utils/format';

// #region STATION
export const selectPOSStations = (state: RootState) =>
  state.posStations.stations;

export const selectPOSstationID = (state: RootState) =>
  state.posStations.selectedStation?.stationID ?? '';

export const selectPOSsubModuleCode = (state: RootState) =>
  state.posStations.selectedStation?.subModuleCode ?? '';

export const selectPOSstationName = (state: RootState) =>
  state.posStations.selectedStation?.stationName;

export const selectPOSSelectedStation = (state: RootState) =>
  state.posStations.selectedStation;

export const selectPOSShiftID = createSelector(
  [selectPOSstationID, selectPOSStations],
  (stationID, stations) => stations[stationID]?.shiftID ?? '',
);

export const selectPOSStationShift = createSelector(
  [selectPOSstationID, selectPOSStations],
  (stationID, stations) => Boolean(stations[stationID]?.shiftID),
);

export const selectPOSShiftRole = createSelector(
  [selectPOSstationID, selectPOSStations],
  (stationID, stations) => stations[stationID]?.selectedRole,
);

export const selectPOSPrinter = createSelector(
  [selectPOSstationID, selectPOSStations],
  (stationID, stations) => stations[stationID]?.selectedPrinter,
);

// #endregion

// #region TABLE SELECTOR
export const selectPOSTableRefID = (state: RootState) =>
  state.posTableArea?.selectedTable?.tableRefID ?? '';

export const selectPOSTableTransactionID = (state: RootState) =>
  state.posTableArea?.transactionDetails?.transaction_id ?? '';

export const selectPOSTableTransactions = createSelector(
  [(state: RootState) => state.posTableArea?.tableTransactions],
  (tableTransactions) => tableTransactions,
);

const selectOrders = (state: RootState) =>
  state.posTableArea?.transactionDetails?.orders ?? [];

export const selectPOSTableOrders = createSelector([selectOrders], (orders) => {
  const normalized = orders.map((order) => {
    const quantity = parseNumber(order.quantity?.toString());
    const grossAmount = parseNumber(order.grossAmount?.toString());
    const derivedUnitPrice = quantity ? grossAmount / quantity : 0;

    return {
      ...order,
      isPrintedOS: Boolean(order.isPrintedOS),
      quantity,
      netAmount: parseNumber(order.netAmount?.toString()),
      grossAmount,
      unitPrice: parseNumber(
        order.unitPrice?.toString() ?? derivedUnitPrice.toString(),
      ),
      isVaryingPrice: Boolean(order.isVaryingPrice),
      discountTotal: parseNumber(order.discountTotal?.toString() ?? '0'),
    };
  });

  // Sort: orders with orderID first, no-orderID last
  return normalized.sort((a, b) => {
    const aHas = Boolean(a.orderID);
    const bHas = Boolean(b.orderID);

    // true = 1, false = 0 → so we invert
    return Number(bHas) - Number(aHas);
  });
});

export const selectPOSisHaveOrders = createSelector(
  [selectOrders],
  (orders) =>
    (orders.find((item) => !item.isPrintedOS && !item.orderID) &&
      orders.length > 0) ??
    false,
);

export const selectPOSTableDetails = createSelector(
  [(state: RootState) => state.posTableArea?.tableDetails],
  (details) => details,
);

export const selectPOSSelectedTable = createSelector(
  [(state: RootState) => state.posTableArea?.selectedTable],
  (table) => table,
);

export const selectPOSServiceOptions = createSelector(
  [(state: RootState) => state.posTableArea.serviceOptions],
  (options) => options,
);

export const selectPOSTableAreas = createSelector(
  [(state: RootState) => state.posTableArea.areas ?? []],
  (areas) => areas,
);

export const selectPOSTableList = createSelector(
  [(state: RootState) => state.posTableArea.tableList ?? []],
  (tableList) => tableList,
);

export const selectPOSTablePaymentDetails = createSelector(
  [(state: RootState) => state.posTableArea.paymentDetails ?? []],
  (paymentDetails) => paymentDetails,
);

export const selectPaymentDetailsByTransaction = createSelector(
  [selectPOSTablePaymentDetails, selectPOSTableTransactionID],
  (paymentDetails, transactionID) => {
    if (!transactionID) return undefined;
    return paymentDetails.find((p) => p.transactionID === transactionID);
  },
);

export const selectPaymentDetailsCountByTransaction = createSelector(
  [selectPaymentDetailsByTransaction],
  (paymentDetails) => {
    if (!paymentDetails) {
      return {
        card: 0,
        ePayment: 0,
        others: 0,
        total: 0,
        amount: {
          card: 0,
          ePayment: 0,
          others: 0,
          total: 0,
        },
        referenceCount: {
          card: {},
          ePayment: {},
          others: {},
        },
      };
    }

    const cardCount = paymentDetails.card?.length ?? 0;
    const ePaymentCount = paymentDetails.ePayment?.length ?? 0;
    const othersCount = paymentDetails.others?.length ?? 0;

    // Helper to count per referenceID
    const countByRef = (items: { referenceID: string }[] = []) =>
      items.reduce<Record<string, number>>((acc, { referenceID }) => {
        if (referenceID) {
          acc[referenceID] = (acc[referenceID] ?? 0) + 1;
        }
        return acc;
      }, {});

    // Helper to safely sum string amounts
    const sumAmount = (items: { amount?: string }[] = []) =>
      items.reduce(
        (sum, { amount }) => sum + (parseFloat(amount ?? '0') || 0),
        0,
      );

    const cardAmount = sumAmount(paymentDetails.card);
    const ePaymentAmount = sumAmount(paymentDetails.ePayment);
    const othersAmount = sumAmount(paymentDetails.others);

    return {
      card: cardCount,
      ePayment: ePaymentCount,
      others: othersCount,
      total: cardCount + ePaymentCount + othersCount,
      amount: {
        card: cardAmount,
        ePayment: ePaymentAmount,
        others: othersAmount,
        total: cardAmount + ePaymentAmount + othersAmount,
      },
      referenceCount: {
        card: countByRef(paymentDetails.card),
        ePayment: countByRef(paymentDetails.ePayment),
        others: countByRef(paymentDetails.others),
      },
    };
  },
);

export const selectPOSIsLoading = (state: RootState) =>
  state.posTableArea.isLoadingTransferOrder;

export const selectPOSTableOrdersTotal = createSelector(
  [selectPOSTableOrders, selectPOSTableDetails],
  (orders, tableDetails) => {
    const tableNet = parseNumber(tableDetails?.netAmount ?? '0');

    // Always consider net for fixed discounts setting net to 0
    const extraCost = orders
      .filter((item) => !item.orderID)
      .reduce((total, item) => total + (item.grossAmount || 0), 0);

    return tableNet + extraCost;
  },
);

export const selectPOSHasSpecificDiscount = createSelector(
  [selectPOSTableOrders, selectPOSTableDetails],
  (orders, details) => {
    const isGroupComputation = Boolean(details?.isGroupComputation);

    const hasDiscount = orders?.some((item) => item.discountID);

    const isAllHasDiscount = orders?.every((item) => item.discountID);

    return hasDiscount && !isAllHasDiscount && !isGroupComputation;
  },
);

export const selectPOSIsAllHasDiscount = createSelector(
  [selectPOSTableOrders],
  (orders) => orders?.every((item) => item.discountID),
);

// #endregion

// #region MASTERLIST
export const selectPOSMenuMasterlist = createSelector(
  [(state: RootState) => state.posMasterlist.menus ?? []],
  (menus) => menus,
);

export const selectPOSLocationMasterlist = createSelector(
  [(state: RootState) => state.posMasterlist.locations ?? []],
  (locations) => locations,
);

export const selectPOSShiftsMasterlist = createSelector(
  [(state: RootState) => state.posMasterlist.shifts ?? []],
  (shifts) => shifts,
);

export const selectPOSEntriesMasterlist = createSelector(
  [(state: RootState) => state.posMasterlist.billOutEntries ?? []],
  (billOutEntries) => billOutEntries,
);

export const selectPOSPrinterMasterlist = createSelector(
  [(state: RootState) => state.posMasterlist.printers ?? []],
  (printers) => printers,
);

export const selectAccountMasterlist = createSelector(
  [(state: RootState) => state.foBookingAccountsList ?? []],
  (accounts) => accounts,
);

export const selectPOSShowPrinter = createSelector(
  [(state: RootState) => state.posMasterlist.printers ?? []],
  (printers) => printers.length > 1,
);

export const selectPOSMenuImages = createSelector(
  [(state: RootState) => state.posMasterlist.menuImages ?? []],
  (menuImages) => menuImages,
);

export const selectPOSMenuLoading = (state: RootState) =>
  state.posMasterlist.isLoadingMenuImages;

// #endregion

// #region SHIFT
export const selectPOSInitialShift = (state: RootState) =>
  state.posStations.initialShift;

export const selectPOSInitialRole = (state: RootState) =>
  state.posStations.initialRole ?? '';

export const selectPOSInitialPrinter = (state: RootState) =>
  state.posStations.initialPrinter ?? '';

export const selectPOSHasSelectRole = (state: RootState) =>
  state.posStations.hasSelectRole;

export const selectPOSHasInitialShift = (state: RootState) =>
  state.posStations.hasInitialShift;

export const selectPOSAvailableShifts = createSelector(
  [selectPOSShiftsMasterlist],
  (shifts) => {
    const opening = shifts.find((item) =>
      item.workPeriod.toLowerCase().includes('opening'),
    );

    const midShift = shifts.find((item) => {
      const workPeriod = item.workPeriod.toLowerCase();

      return (
        workPeriod.includes('mid-shift') || workPeriod.includes('mid shift')
      );
    });

    const closing = shifts.find((item) =>
      item.workPeriod.toLowerCase().includes('closing'),
    );

    return {
      1: opening,
      2: midShift,
      3: closing,
    };
  },
);

export const selectPOSHasPrinter = (state: RootState) =>
  state.posStations.hasPrinter;
// #endregion
